import { forwardRef } from 'react';
import { Megaphone, Cookie } from 'lucide-react';
import { GoogleAdSense } from './GoogleAdSense';
import { useTermsAccepted } from '@/hooks/useTermsAccepted';
import { useCookieConsent, getCookiePreferences } from './CookieConsent';

type AdBannerProps = {
  adSlot?: string;
  adFormat?: 'auto' | 'rectangle' | 'horizontal' | 'vertical';
  className?: string;
};

export const AdBanner = forwardRef<HTMLDivElement, AdBannerProps>(
  ({ adSlot, adFormat = 'auto', className = '' }, ref) => {
    const termsAccepted = useTermsAccepted();
    const cookieConsent = useCookieConsent();

    // Don't show ads until user accepts terms
    if (!termsAccepted) {
      return (
        <div ref={ref} className={`flex items-center justify-center ${className}`}>
          <div className="flex items-center justify-center gap-3 px-6 py-4 rounded-xl bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border border-border/30">
            <Megaphone className="w-5 h-5 text-primary opacity-50" />
            <span className="text-sm text-muted-foreground/70 font-medium">
              Accept terms to view content
            </span>
          </div>
        </div>
      );
    }

    // Check if advertising cookies are allowed
    const preferences = getCookiePreferences();
    const advertisingAllowed = cookieConsent === 'accepted' || 
      (cookieConsent === 'custom' && preferences.advertising);

    // Don't show ads if user hasn't consented or rejected advertising
    if (cookieConsent === 'pending') {
      return (
        <div ref={ref} className={`flex items-center justify-center ${className}`}>
          <div className="flex items-center justify-center gap-3 px-6 py-4 rounded-xl bg-gradient-to-r from-secondary/50 via-muted/50 to-secondary/50 border border-border/30">
            <Cookie className="w-5 h-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground font-medium">
              Accept cookies for ads
            </span>
          </div>
        </div>
      );
    }

    if (!advertisingAllowed) {
      return (
        <div ref={ref} className={`flex items-center justify-center ${className}`}>
          <div className="flex items-center justify-center gap-3 px-6 py-4 rounded-xl bg-gradient-to-r from-secondary/50 via-muted/50 to-secondary/50 border border-border/30">
            <Cookie className="w-5 h-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground font-medium">
              Ads disabled
            </span>
          </div>
        </div>
      );
    }

    return (
      <div ref={ref} className={`flex items-center justify-center ${className}`}>
        <GoogleAdSense adSlot={adSlot || ''} adFormat={adFormat} />
      </div>
    );
  }
);

AdBanner.displayName = 'AdBanner';
